/**
 * The composer's settings gear: a popover of switches for how the assistant runs
 * (see ./chatSettings for the state and where each switch is read).
 *
 * Registered as the styled Thread's `ComposerActionExtra` slot, so it sits in the
 * composer action row immediately before the mic and send buttons — next to the
 * controls it modifies, rather than in a global preferences screen the user would
 * have to leave the conversation to find.
 *
 * A popover of switches rather than a dropdown of menu items: these are persistent
 * modes, not actions, and a switch shows its current state without being opened.
 * assistant-ui has no settings surface of its own (its menu primitives are bound to
 * the action bar and thread list), so this is ordinary composition — its only
 * contribution is the slot.
 *
 * Every switch is phrased POSITIVELY ("Enable …") and shown exactly as it is stored, so
 * a switch that is on means the thing it names is on — no inversion to keep straight
 * between here and the wire.
 *
 * The closed trigger says nothing about the current settings: it's the plain gear whether
 * or not the user has changed anything. A badge on it would have to mean "not the
 * defaults", which isn't a state worth an always-visible mark in the composer row — the
 * switches themselves are one click away and each shows its own state.
 */
import { Popover, PopoverContent, PopoverTrigger } from "@mdotm/shadcn/ui";
import { TooltipIconButton } from "@mdotm/shadcn/assistant-ui";
import { textDataAttr } from "@mdotm/shadcn/legacy/components";
import { useId } from "react";
import { useUserValue } from "$root/functional-areas/user";
import { useSphereAIPanelPortal } from "./SphereAIPanelPortal";
import { useChatSettings } from "./chatSettings";
import { Switch } from "@mdotm/shadcn/mdotm/components";

/**
 * lucide-react's `Settings2` glyph, inlined. lucide is a dependency of the shadcn
 * bundle, not of the frontend, so sphere-ai inlines the glyphs it needs — the same
 * thing the choice recap's pencil does.
 */
function SettingsIcon({ className }: { className?: string }): React.JSX.Element {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M20 7h-9m3 10H5" />
			<circle cx="17" cy="17" r="3" />
			<circle cx="7" cy="7" r="3" />
		</svg>
	);
}

/**
 * One setting: label, one line on what turning it on does, and the switch.
 *
 * The control is `mdotm/Switch` since MDOT-24854. It was the legacy `Checkbox`'s
 * `switchType="switch"` variant, picked because it fills with `--palette-P500` when on — the
 * platform's "on" green, which `--primary` also maps to (styles/customizations.css), so it
 * agrees with the primary controls around it. That reason still holds and no longer needs an
 * exception: `src/ui/switch.tsx` is styled to exactly that variable, so the modern component
 * is the same green.
 */
function SettingRow({
	label,
	description,
	checked,
	onChange,
	qualifier,
}: {
	label: string;
	description: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	qualifier: string;
}): React.JSX.Element {
	// `Switch` forwards only `data-*` attributes, so it can't take an `aria-label`; it wires
	// its own <label> to the control by id instead. Naming it therefore means a SECOND
	// <label htmlFor> on the visible text — legal, and deliberate: it is what makes that text
	// click-to-toggle, which is the whole affordance of this row.
	//
	// Since MDOT-24854 the `for` target is a <button role="switch">, not a hidden <input>, so
	// the control's accessible name is computed from BOTH labels. Harmless here — they say the
	// same thing — but it does make an unscoped getByLabel() ambiguous. e2e reaches this row by
	// `data-qualifier`, never by label.
	const inputId = useId();
	return (
		<div className="flex items-start justify-between gap-4">
			<div className="flex flex-col gap-0.5">
				{/* Pitched to the THREAD's scale, not the platform's: the panel deliberately runs one step
				    above the app's running text (Body/L prose, Body/M secondary), and this popover belongs
				    to the panel. At Body/M + Body/S it read two steps down from the conversation it
				    configures. See SphereAIPanelPortal for why the colours now agree too. */}
				<label htmlFor={inputId} {...textDataAttr("Body/L/Book")}>
					{label}
				</label>
				<span {...textDataAttr("Body/M/Book")} className="text-muted-foreground">
					{description}
				</span>
			</div>
			<Switch id={inputId} checked={checked} onChange={onChange} data-qualifier={qualifier} className="mt-1 shrink-0" />
		</div>
	);
}

export function SphereAIComposerSettings(): React.JSX.Element {
	const { settings, setSetting } = useChatSettings();
	// The customer-level ceiling for web search. It is a ceiling, not a default: the
	// switch below can only opt OUT beneath it, so with the grant withheld the row is
	// hidden rather than shown-and-disabled — a control that cannot change anything
	// reads as something the user might be able to unlock, which they cannot. The
	// server enforces it either way (sphere-mcp's resolveWebSearchEnabled), so a
	// stored `webSearch: true` from before the grant was withdrawn is harmless.
	const { customerWebSearchEnabled: webSearchAllowed } = useUserValue();

	// Portal into the panel instead of `document.body`. Radix's default target puts this content
	// outside `.sphere-ai-thread`, where none of the panel's token remapping reaches it — the popover
	// then falls back to the app-wide shadcn defaults and reads as a foreign surface. See
	// SphereAIPanelPortal.
	const portalContainer = useSphereAIPanelPortal();

	return (
		<Popover>
			<PopoverTrigger asChild>
				<TooltipIconButton
					tooltip="Assistant settings"
					side="bottom"
					type="button"
					variant="ghost"
					size="icon"
					// `sphere-ai-composer-settings` joins the row's icon buttons, which take the platform's
					// button shape in sphere-ai-thread.css; `rounded-full` stays as the fallback for
					// anywhere that stylesheet isn't loaded.
					className="sphere-ai-composer-settings size-7 rounded-full"
					aria-label="Assistant settings"
					data-qualifier="SphereAI/Composer/Settings"
				>
					{/* `sphere-ai-composer-settings-icon` puts this in the set of muted line-style composer
				    icons alongside the `+` and the mic, which are coloured together in
				    sphere-ai-thread.css. Without it the gear inherited the button's N700 and read heavier
				    than the two beside it. Size and stroke were already normalised there, by the rule on
				    the action row's svgs. */}
					<SettingsIcon className="sphere-ai-composer-settings-icon size-4" />
				</TooltipIconButton>
			</PopoverTrigger>
			{/* Above the composer and right-aligned to the gear: the composer sits at the
			    bottom of the thread, so a popover below it would open off-panel. */}
			<PopoverContent container={portalContainer} side="top" align="end" className="w-80 gap-4 p-4">
				{/* Phrased and stored the same way round: each switch shows the stored (positive)
				    setting directly, which is also what the request body and the MCP header
				    carry. A chat nobody has touched shows the defaults: thinking on, web search
				    off. */}
				<SettingRow
					label="Enable extended thinking"
					description="Reasons before answering. Slower, stronger on multi-step questions."
					checked={settings.thinking}
					onChange={(enabled) => setSetting("thinking", enabled)}
					qualifier="SphereAI/Composer/Settings/Thinking"
				/>
				{webSearchAllowed && (
					<SettingRow
						label="Enable web search"
						description="Lets the assistant read public web pages it's pointed at."
						checked={settings.webSearch}
						onChange={(enabled) => setSetting("webSearch", enabled)}
						qualifier="SphereAI/Composer/Settings/WebSearch"
					/>
				)}
			</PopoverContent>
		</Popover>
	);
}
